// Deliveries received (M6): recorded at the receiving station, against an order or without
// one. What didn't match is flagged, never refused (D-060); the flagged ones are where a
// person looks first. A delivery the server couldn't book as sent is held for a manager
// (D-066): it is listed first, as held, with the way to book it (UI6A-102).

import type { ReceiptSummary } from '@enp/domain/api';
import {
  Badge,
  Button,
  cn,
  createColumnHelper,
  DataTable,
  EmptyState,
  PageHeader,
  type ColumnDef,
} from '@enp/ui';
import { useInfiniteQuery, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronRight, PackageCheck, SearchX, TriangleAlert, X } from 'lucide-react';
import { useMemo } from 'react';
import { meQueryOptions } from '../../../../auth/session.js';
import { HeldDeliveryList } from '../../../../purchasing/held.js';
import {
  KeyHints,
  PurchasingViews,
  Section,
  TableSkeleton,
  usePageKeys,
} from '../../../../purchasing/parts.js';
import {
  allSuppliersQuery,
  heldDeliveriesQuery,
  type ReceiptsSearch,
  receiptsQuery,
  receiptsSearchSchema,
} from '../../../../purchasing/queries.js';
import { AccountDate } from '../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { DayRange, FilterSelect, LoadMoreFooter } from '../../../../stock/components.js';
import { accountToday } from '../../../../stock/labels.js';
import { clean } from '../../../../stock/queries.js';

export const Route = createFileRoute('/_app/purchasing/receipts/')({
  validateSearch: receiptsSearchSchema,
  component: ReceiptsPage,
});

const col = createColumnHelper<ReceiptSummary>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<ReceiptSummary, any>[] = [
  col.display({
    id: 'number',
    header: 'Delivery',
    meta: { width: 'min-w-40' },
    cell: ({ row }) => (
      <span className="flex flex-col leading-tight">
        <Link
          to="/purchasing/receipts/$receiptId"
          params={{ receiptId: row.original.id }}
          tabIndex={-1}
          className="whitespace-nowrap font-mono text-sm font-medium text-fg hover:underline"
        >
          {row.original.number}
        </Link>
        <span className="text-xs text-muted">
          {row.original.supplier_doc_ref ? (
            <>
              Their note <span className="whitespace-nowrap">{row.original.supplier_doc_ref}</span>
            </>
          ) : (
            'No delivery note number'
          )}
        </span>
      </span>
    ),
  }),
  col.display({
    id: 'received',
    header: 'Received',
    cell: ({ row }) => (
      <span className="flex flex-col leading-tight text-sm">
        <AccountDate value={row.original.received_at} withTime />
        <span className="text-muted">
          {row.original.received_by?.name ?? 'Someone at a station'}
        </span>
      </span>
    ),
  }),
  col.display({
    id: 'supplier',
    header: 'Supplier',
    cell: ({ row }) => (
      <span className="flex flex-col leading-tight">
        <span className="font-medium text-fg">{row.original.supplier?.name ?? 'Not recorded'}</span>
        {row.original.po ? (
          <span className="whitespace-nowrap font-mono text-xs text-muted">
            {row.original.po.number}
          </span>
        ) : (
          <span className="text-xs text-muted">Without an order</span>
        )}
      </span>
    ),
  }),
  col.display({
    id: 'where',
    header: 'Put away at',
    meta: { className: 'hidden lg:table-cell' },
    cell: ({ row }) => (
      <span className="text-sm text-fg">
        {row.original.branch.name} · {row.original.location.name}
      </span>
    ),
  }),
  col.display({
    id: 'check',
    header: 'Check',
    cell: ({ row }) =>
      row.original.flag_count > 0 ? (
        <Badge tone="warning">
          <TriangleAlert aria-hidden />
          {row.original.flag_count} {row.original.flag_count === 1 ? 'thing' : 'things'} to check
        </Badge>
      ) : (
        <Badge tone="success">
          <PackageCheck aria-hidden />
          Nothing flagged
        </Badge>
      ),
  }),
  col.display({
    id: 'open',
    header: () => <span className="sr-only">Open</span>,
    meta: { width: 'w-8' },
    cell: () => <ChevronRight aria-hidden className="size-4 text-muted" />,
  }),
];

function ReceiptsPage() {
  useDocumentTitle('Deliveries');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const suppliers = useQuery(allSuppliersQuery());
  const list = useInfiniteQuery(receiptsQuery(search));
  const held = useQuery(heldDeliveriesQuery(search.po));
  // Held deliveries follow the supplier filter; a day range or "only flagged" is about booked
  // deliveries, and a held one is always something to check.
  const heldItems = (held.data?.items ?? []).filter(
    (h) => !search.supplier || h.supplier?.id === search.supplier,
  );
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const filtered = Boolean(
    search.from || search.to || search.supplier || search.po || search.flagged,
  );
  const setSearch = (patch: Partial<ReceiptsSearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });
  const toggleFlagged = () => setSearch({ flagged: search.flagged ? undefined : true });
  // j / k: to the next or previous delivery with something to check (UI6A-18); f: only those.
  const moveFlagged = (step: 1 | -1) => {
    const rows = Array.from(
      document.querySelectorAll<HTMLTableRowElement>('table[aria-label="Deliveries"] tbody tr'),
    );
    const current = rows.findIndex((row) => row.contains(document.activeElement));
    const flagged = items.flatMap((it, i) => (it.flag_count > 0 ? [i] : []));
    const target =
      step === 1
        ? flagged.find((i) => i > current)
        : [...flagged].reverse().find((i) => i < (current === -1 ? items.length : current));
    if (target !== undefined) rows[target]?.focus();
  };
  usePageKeys({
    j: items.length > 0 ? () => moveFlagged(1) : undefined,
    k: items.length > 0 ? () => moveFlagged(-1) : undefined,
    f: toggleFlagged,
  });

  const empty = filtered ? (
    <EmptyState
      compact
      icon={SearchX}
      title={search.flagged ? 'Nothing flagged' : 'No deliveries match'}
      description={
        search.flagged
          ? 'No delivery in these filters has anything to check.'
          : 'No delivery fits these filters.'
      }
      action={
        <Button
          variant="secondary"
          icon={<X aria-hidden />}
          onClick={() => void navigate({ search: {} })}
        >
          Clear filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={PackageCheck}
      title="No deliveries received yet"
      description="Deliveries are received at the receiving station: counted at the door, with the supplier’s lot and best-before, into labelled lots. Each one shows here, with anything that didn’t match the order."
      action={
        <Button variant="secondary" asChild>
          <Link to="/purchasing">See orders waiting for delivery</Link>
        </Button>
      }
    />
  );

  return (
    <>
      <PageHeader
        title="Purchasing"
        description="Deliveries recorded at the receiving station. Anything that didn’t match the order is flagged, with what to do; nothing is ever refused at the door."
      />
      <PurchasingViews current="receipts" />
      <div
        className="mb-3 flex flex-wrap items-center gap-2"
        role="search"
        aria-label="Delivery filters"
      >
        <FilterSelect
          label="Supplier"
          value={search.supplier}
          allLabel="All suppliers"
          options={(suppliers.data?.items ?? []).map((s) => ({ value: s.id, label: s.name }))}
          onChange={(v) => setSearch({ supplier: v })}
        />
        <DayRange
          idPrefix="receipts"
          from={search.from}
          to={search.to}
          today={today}
          onChange={(r) => setSearch(r)}
        />
        <Button
          variant="secondary"
          aria-pressed={Boolean(search.flagged)}
          className={cn(search.flagged && 'border-primary/60 bg-primary-subtle text-primary-text')}
          icon={<TriangleAlert aria-hidden />}
          aria-keyshortcuts="f"
          onClick={toggleFlagged}
        >
          Only with something to check
        </Button>
        {filtered && (
          <Button
            variant="ghost"
            icon={<X aria-hidden />}
            onClick={() => void navigate({ search: {} })}
          >
            Clear
          </Button>
        )}
        <span className="ml-auto">
          <KeyHints
            keys={[
              ['j', 'next to check'],
              ['k', 'previous'],
              ['f', 'only to check'],
            ]}
          />
        </span>
      </div>
      {heldItems.length > 0 && (
        <Section
          id="held-deliveries"
          title={`Held, not booked yet (${heldItems.length})`}
          className="mb-4 border-state-warning/50"
        >
          <p className="text-sm text-muted">
            Recorded at a station, but the server couldn’t book them as sent. They wait in the held
            actions for a manager to correct and book them; until then they are not in stock.
          </p>
          <HeldDeliveryList items={heldItems} />
        </Section>
      )}
      {list.isError && !list.data ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The deliveries"
          onRetry={() => void list.refetch()}
        />
      ) : list.isPending ? (
        <TableSkeleton
          label="Deliveries"
          columns={[
            { header: 'Delivery', className: 'min-w-40', twoLines: true },
            { header: 'Received', twoLines: true },
            { header: 'Supplier', twoLines: true },
            { header: 'Put away at', className: 'hidden lg:table-cell' },
            { header: 'Check' },
            { header: '', className: 'w-8', empty: true },
          ]}
        />
      ) : (
        <DataTable
          label="Deliveries"
          columns={columns}
          data={items}
          loading={false}
          empty={empty}
          filterable={false}
          getRowId={(r) => r.id}
          onRowActivate={(r) =>
            void navigate({ to: '/purchasing/receipts/$receiptId', params: { receiptId: r.id } })
          }
          footer={
            items.length > 0 ? (
              <LoadMoreFooter
                loaded={items.length}
                noun={['delivery', 'deliveries']}
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                updating={list.isFetching && !list.isFetchingNextPage}
                failed={list.isError && Boolean(list.data)}
                onMore={() => void list.fetchNextPage()}
              />
            ) : undefined
          }
          className="xl:max-h-[calc(100dvh-22rem)]"
        />
      )}
    </>
  );
}
